import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {
  Fade, Tabs, Tab, TextField,
} from '@material-ui/core';
import { get } from 'lodash/fp';
import { FastField, Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Select, Snackbar,
} from '../../../common/components';

const grades = [
  { id: '1', label: '1학년', value: '1' },
  { id: '2', label: '2학년', value: '2' },
  { id: '3', label: '3학년', value: '3' },
  { id: '4', label: '4학년', value: '4' },
  { id: '5', label: '5학년', value: '5' },
  { id: '6', label: '6학년', value: '6' },
];

const ClazzGroupSchema = Yup.object().shape({
  grade: Yup.string()
    .required(),
  clazzName: Yup.string()
    .required(),
});

const CustomGroupSchema = Yup.object().shape({
  name: Yup.string()
    .max(8)
    .required(),
});

const S = {
  Dialog: styled(Dialog)`
    .paper {
      position: relative;
      width: 480px;
      padding: 32px 40px 24px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    margin-bottom: 0.75rem;
  `,
  Heading: styled.h3`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(20)};
    font-weight: 500;
    line-height: 1.5;
  `,
  DialogContent: styled(DialogContent)`
    margin-bottom: 40px;
  `,
  Tabs: styled(Tabs)`
    border-bottom: 1px solid ${({ theme }) => theme.palette.grey[300]};
    .MuiTabs-indicator {
      height: 1px;
    }
  `,
  Tab: styled(Tab)`
    padding: 12px 8px;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
    &.Mui-selected .MuiTab-wrapper {
      font-weight: 700;
    }
  `,
  TabView: styled.div`
    display: flex;
    padding-top: 40px;
  `,
  Item: styled.div`
    width: 50%;
    :first-child {
      padding-right: 8px;
    }
    :last-child {
      padding-left: 8px;
    }
  `,
  Select: styled(Select)`
    width: 100%;
  `,
  TextField: styled(({ InputLabelProps, ...otherProps }) => (
    <TextField
      InputLabelProps={{
        ...InputLabelProps,
        classes: { root: 'label-root' },
      }}
      {...otherProps}
    />
  ))`
    width: 100%;
    margin: 0;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    .label-root {
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.palette.grey[300]};
    }
  `,
  DialogActions: styled(DialogActions)`
    display: flex;
  `,
};

const GroupAddDialog = ({
  creationTask, createClazzGroup, createCustomGroup, initCreationTask, match, open, onClose,
}) => {
  const [tab, setTab] = useState(0);

  const handleChange = useCallback((event, value) => setTab(value), []);
  const handleSubmit = useCallback(values => (tab === 0
    ? createClazzGroup({ sid: match.params.sid, grade: values.grade, clazzName: values.clazzName })
    : createCustomGroup({ sid: match.params.sid, name: values.name })
  ), [createClazzGroup, createCustomGroup, match.params.sid, tab]);

  useEffect(() => {
    if (creationTask.data) {
      onClose();

      const timeoutId = setTimeout(initCreationTask, 3000);

      return () => clearTimeout(timeoutId);
    }

    return () => {};
  }, [creationTask.data, initCreationTask, onClose]);

  return (
    <>
      <S.Dialog open={open} onClose={onClose}>
        <S.DialogTitle>
          <S.Heading>
            새 그룹 추가
          </S.Heading>
        </S.DialogTitle>
        <Formik
          initialValues={{ grade: '', clazzName: '', name: '' }}
          validationSchema={tab === 0 ? ClazzGroupSchema : CustomGroupSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {() => (
            <Form>
              <S.DialogContent>
                <S.Tabs
                  value={tab}
                  onChange={handleChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <S.Tab
                    label="반 추가"
                    disableRipple
                  />
                  <S.Tab
                    label="사용자 그룹 추가"
                    disableRipple
                  />
                </S.Tabs>
                <S.TabView>
                  {tab === 0
                    ? (
                      <>
                        <S.Item>
                          <FastField name="grade">
                            {({ field, form, ...otherProps }) => (
                              <S.Select
                                label="학년"
                                options={grades}
                                error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                                {...field}
                                {...otherProps}
                              />
                            )}
                          </FastField>
                        </S.Item>
                        <S.Item>
                          <FastField name="clazzName">
                            {({ field, form, ...otherProps }) => (
                              <S.TextField
                                label="반"
                                margin="dense"
                                variant="outlined"
                                error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                                {...field}
                                {...otherProps}
                              />
                            )}
                          </FastField>
                        </S.Item>
                      </>
                    )
                    : (
                      <FastField name="name">
                        {({ field, form, ...otherProps }) => (
                          <S.TextField
                            label="그룹명"
                            margin="dense"
                            variant="outlined"
                            error={!!(get(field.name, form.errors) && get(field.name, form.touched))}
                            {...field}
                            {...otherProps}
                          />
                        )}
                      </FastField>
                    )}
                </S.TabView>
              </S.DialogContent>
              <S.DialogActions>
                <Button type="button" variant="outlined" onClick={onClose}>
                  취소
                </Button>
                <Button type="submit" color="primary" variant="contained">
                  저장
                </Button>
              </S.DialogActions>
            </Form>
          )}
        </Formik>
      </S.Dialog>
      <Snackbar
        open={!!creationTask.data}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'create-success-message',
        }}
        message={(
          <span id="create-success-message">
            {/* TODO: 포스트 응답 데이터를 저장하여 어떤 그룹이 생성되었는지 출력 필요 */}
            {'새 그룹이 생성되었습니다.'}
          </span>
        )}
      />
    </>
  );
};

GroupAddDialog.propTypes = {
  creationTask: PropTypes.shape({ data: null, loading: false, error: null }).isRequired,
  createClazzGroup: PropTypes.func.isRequired,
  createCustomGroup: PropTypes.func.isRequired,
  initCreationTask: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};


export default withRouter(GroupAddDialog);
